import type { PublicCalendarEntry } from "@/api/public";

export type DayState = { morning: boolean; afternoon: boolean };

export type OccupancyMap = Map<string, Map<string, DayState>>;
// unit name -> isoDate -> DayState

function isoKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function parseIso(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function ensureDay(unitMap: Map<string, DayState>, key: string): DayState {
  let s = unitMap.get(key);
  if (!s) {
    s = { morning: false, afternoon: false };
    unitMap.set(key, s);
  }
  return s;
}

// Halbtag-Konvention:
//  Anreisetag ab check_in_time (default 15:00) -> nachmittags belegt.
//  Abreisetag bis check_out_time (default 10:00) -> vormittags belegt.
//  Tage dazwischen -> ganztägig belegt.
//  Wenn check_in_time <= 12:00, gilt der Anreisetag bereits vormittags als belegt.
//  Wenn check_out_time >= 13:00, gilt der Abreisetag auch nachmittags als belegt.
export function buildOccupancy(entries: PublicCalendarEntry[]): OccupancyMap {
  const out: OccupancyMap = new Map();
  for (const e of entries) {
    const unit = e.einheit_name;
    if (!unit) continue;
    let unitMap = out.get(unit);
    if (!unitMap) {
      unitMap = new Map();
      out.set(unit, unitMap);
    }
    const cin = parseIso(e.check_in);
    const cout = parseIso(e.check_out);
    if (isNaN(cin.getTime()) || isNaN(cout.getTime())) continue;
    if (cout.getTime() <= cin.getTime()) continue;

    const ciTime = (e.check_in_time ?? "15:00").slice(0, 5);
    const coTime = (e.check_out_time ?? "10:00").slice(0, 5);
    const ciHour = parseInt(ciTime.split(":")[0] || "15", 10);
    const coHour = parseInt(coTime.split(":")[0] || "10", 10);

    // Anreisetag
    const ciKey = isoKey(cin);
    const ciDay = ensureDay(unitMap, ciKey);
    ciDay.afternoon = true;
    if (ciHour < 13) ciDay.morning = true;

    // Volle Tage dazwischen
    const cursor = new Date(cin);
    cursor.setDate(cursor.getDate() + 1);
    while (cursor.getTime() < cout.getTime()) {
      const k = isoKey(cursor);
      const d = ensureDay(unitMap, k);
      d.morning = true;
      d.afternoon = true;
      cursor.setDate(cursor.getDate() + 1);
    }

    // Abreisetag
    const coKey = isoKey(cout);
    const coDay = ensureDay(unitMap, coKey);
    coDay.morning = true;
    if (coHour >= 13) coDay.afternoon = true;
  }
  return out;
}

export function isoDate(year: number, month1: number, day: number): string {
  const m = String(month1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}
